import whaleImg from "@/assets/whale-migration.jpg";
import whaleAvif640 from "@/assets/whale-migration-640.avif";
import whaleAvif1024 from "@/assets/whale-migration-1024.avif";
import whaleAvif1600 from "@/assets/whale-migration-1600.avif";
import whaleWebp640 from "@/assets/whale-migration-640.webp";
import whaleWebp1024 from "@/assets/whale-migration-1024.webp";
import whaleWebp1600 from "@/assets/whale-migration-1600.webp";

import sealImg from "@/assets/seal-pupping.jpg";
import sealAvif640 from "@/assets/seal-pupping-640.avif";
import sealAvif1024 from "@/assets/seal-pupping-1024.avif";
import sealAvif1600 from "@/assets/seal-pupping-1600.avif";
import sealWebp640 from "@/assets/seal-pupping-640.webp";
import sealWebp1024 from "@/assets/seal-pupping-1024.webp";
import sealWebp1600 from "@/assets/seal-pupping-1600.webp";

import tidesImg from "@/assets/king-tides.jpg";
import tidesAvif640 from "@/assets/king-tides-640.avif";
import tidesAvif1024 from "@/assets/king-tides-1024.avif";
import tidesAvif1600 from "@/assets/king-tides-1600.avif";
import tidesWebp640 from "@/assets/king-tides-640.webp";
import tidesWebp1024 from "@/assets/king-tides-1024.webp";
import tidesWebp1600 from "@/assets/king-tides-1600.webp";

import grunionImg from "@/assets/grunion-run.jpg";
import grunionAvif640 from "@/assets/grunion-run-640.avif";
import grunionAvif1024 from "@/assets/grunion-run-1024.avif";
import grunionAvif1600 from "@/assets/grunion-run-1600.avif";
import grunionWebp640 from "@/assets/grunion-run-640.webp";
import grunionWebp1024 from "@/assets/grunion-run-1024.webp";
import grunionWebp1600 from "@/assets/grunion-run-1600.webp";

import orchidConservatoryImg from "@/assets/orchids/orchid-conservatory.jpg";

import laJollaCoveImg from "@/assets/beach-la-jolla-cove.webp";
import coronadoImg from "@/assets/beach-coronado.webp";
import sunsetCliffsImg from "@/assets/beach-sunset-cliffs.webp";
import torreyPinesImg from "@/assets/trail-torrey-pines.webp";
import coastWalkImg from "@/assets/trail-coast-walk.webp";
import snorkelingImg from "@/assets/activity-snorkeling.webp";
import surfingImg from "@/assets/activity-surfing.webp";
import lateSummerSurfImg from "@/assets/happening/late-summer-surf-hero.jpg";

import paddleboardImg from "@/assets/activity-paddleboard.webp";
import homepageKayakHeroAsset from "@/assets/kayak-sea-caves-4.jpg.asset.json";
import july4thHeroAsset from "@/assets/july-4th/hero-big-bay-boom.jpg.asset.json";
import bioluminescentHeroImg from "@/assets/bioluminescent-waves.jpg";

const homepageKayakHeroImg = (homepageKayakHeroAsset as { url: string }).url;
const july4thHeroImg = (july4thHeroAsset as { url: string }).url;

interface ResponsiveSet {
  avif: { w640: string; w1024: string; w1600: string };
  webp: { w640: string; w1024: string; w1600: string };
}

const whaleSet: ResponsiveSet = {
  avif: { w640: whaleAvif640, w1024: whaleAvif1024, w1600: whaleAvif1600 },
  webp: { w640: whaleWebp640, w1024: whaleWebp1024, w1600: whaleWebp1600 },
};
const sealSet: ResponsiveSet = {
  avif: { w640: sealAvif640, w1024: sealAvif1024, w1600: sealAvif1600 },
  webp: { w640: sealWebp640, w1024: sealWebp1024, w1600: sealWebp1600 },
};
const tidesSet: ResponsiveSet = {
  avif: { w640: tidesAvif640, w1024: tidesAvif1024, w1600: tidesAvif1600 },
  webp: { w640: tidesWebp640, w1024: tidesWebp1024, w1600: tidesWebp1600 },
};
const grunionSet: ResponsiveSet = {
  avif: { w640: grunionAvif640, w1024: grunionAvif1024, w1600: grunionAvif1600 },
  webp: { w640: grunionWebp640, w1024: grunionWebp1024, w1600: grunionWebp1600 },
};

export interface HappeningStory {
  id: string;
  /** Inclusive recurring start date (MM-DD). */
  startDate: string;
  /** Inclusive recurring end date (MM-DD). */
  endDate: string;
  /** Higher priority wins when multiple stories overlap. Defaults to 0. */
  priority?: number;
  badge: string;
  title: string;
  body: string;
  /** Human-friendly date label shown in the card (e.g. "Through April"). */
  date: string;
  location: string;
  image: string;
  sources?: ResponsiveSet;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
  cta: { label: string; to: string; external?: boolean };
  /** Optional internal page the entire card links to (overrides cta.to for card click). */
  pageHref?: string;
}

const standardImageSize = { imageWidth: 1600, imageHeight: 1067 };

/**
 * Seasonal story queue — recurring local hooks first. Add more entries any time;
 * the component automatically picks the active one based on the visitor's date.
 */
export const stories: HappeningStory[] = [
  {
    id: "january-king-tides",
    startDate: "01-01",
    endDate: "01-14",
    priority: 2,
    badge: "This Week",
    title: "King Tides Reveal Sunset Cliffs' Best Tide Pools",
    body:
      "Winter's lowest tide windows expose broad reef shelves at Sunset Cliffs and Cabrillo National Monument. Look for anemones, hermit crabs, sea stars, and sand channels normally hidden by the Pacific.",
    date: "Early January low-tide windows",
    location: "Sunset Cliffs & Cabrillo National Monument",
    image: tidesImg,
    sources: tidesSet,
    imageAlt:
      "Tide pools at Sunset Cliffs San Diego at low tide during golden hour, with a starfish and exposed rocky reef",
    imageWidth: 1920,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/january-king-tides" },
    pageHref: "/happening/january-king-tides",
  },
  {
    id: "gray-whale-southbound",
    startDate: "01-15",
    endDate: "02-29",
    priority: 1,
    badge: "Happening Now",
    title: "Gray Whales Cruise Past Point Loma",
    body:
      "Gray whales are moving along San Diego's nearshore migration corridor, with spouts often visible from Cabrillo National Monument, Torrey Pines, and the La Jolla bluffs on clear mornings.",
    date: "January through February",
    location: "Point Loma to Torrey Pines",
    image: whaleImg,
    sources: whaleSet,
    imageAlt:
      "Point Loma cliffs at golden hour with the Pacific Ocean and a distant gray whale spout on the horizon",
    imageWidth: 1920,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/gray-whale-southbound" },
    pageHref: "/happening/gray-whale-southbound",
  },
  {
    id: "march-bluff-blooms",
    startDate: "03-01",
    endDate: "03-20",
    badge: "This Month",
    title: "Coastal Bluffs Turn Green After Winter Rains",
    body:
      "Torrey Pines and the La Jolla cliff walks are at their freshest, with brighter bluff vegetation, clearer air, and comfortable hiking temperatures before the busy spring-break rush.",
    date: "Early to mid-March",
    location: "Torrey Pines & La Jolla",
    image: torreyPinesImg,
    imageAlt: "Torrey Pines coastal trail overlooking green bluffs and the Pacific Ocean",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/march-bluff-blooms" },
    pageHref: "/happening/march-bluff-blooms",
  },
  {
    id: "spring-coast-walks",
    startDate: "03-21",
    endDate: "04-15",
    badge: "Fresh Pick",
    title: "Spring Beach Walks Hit Their Sweet Spot",
    body:
      "Mild afternoons and lighter crowds make this one of the easiest windows for long shoreline walks from Coronado, Del Mar, Mission Beach, or the La Jolla Coast Walk.",
    date: "Late March through mid-April",
    location: "Coronado to Del Mar",
    image: coastWalkImg,
    imageAlt: "San Diego coastal walking path above ocean cliffs on a clear spring day",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/spring-coast-walks" },
    pageHref: "/happening/spring-coast-walks",
  },
  {
    id: "spring-tidepooling",
    startDate: "04-16",
    endDate: "04-30",
    priority: 1,
    badge: "Low Tide",
    title: "Spring Tide Pools Are Still Worth the Early Start",
    body:
      "April's morning low tides can still open up rocky tide pools at Cabrillo, Sunset Cliffs, and La Jolla. Arrive before the tide turns and keep shoes grippy for slick reef shelves.",
    date: "Late April mornings",
    location: "Cabrillo, Sunset Cliffs & La Jolla",
    image: sunsetCliffsImg,
    imageAlt: "Sunset Cliffs shoreline with rocky shelves and tide pools beside the Pacific Ocean",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/spring-tidepooling" },
    pageHref: "/happening/spring-tidepooling",
  },
  {
    id: "orchids-in-the-wild",
    startDate: "05-02",
    endDate: "06-01",
    priority: 3,
    badge: "Happening Now",
    title: "Orchids in the Wild Bloom at the San Diego Botanic Garden",
    body:
      "The annual Orchids in the Wild showcase fills the Dickinson Family Education Conservatory in Encinitas with naturalistic displays, plant sales, and a special 21+ Orchids at Night evening on May 21.",
    date: "May 2 – June 1, 2026",
    location: "Encinitas — San Diego Botanic Garden",
    image: orchidConservatoryImg,
    imageAlt:
      "Lush orchid display inside a sunlit conservatory greenhouse with hanging epiphytes and vibrant blooms",
    imageWidth: 1600,
    imageHeight: 896,
    cta: { label: "Read the full story", to: "/happening/orchids-in-the-wild" },
    pageHref: "/happening/orchids-in-the-wild",
  },
  {
    id: "harbor-seal-pupping",
    startDate: "05-01",
    endDate: "05-15",
    priority: 2,
    badge: "Happening Now",
    title: "Harbor Seal Pupping Season at La Jolla's Children's Pool",
    body:
      "Newborn harbor seal pups are hauled out on the sand at Casa Beach, where the rope barrier protects mothers and pups. The overlook above the seawall offers a close, respectful view.",
    date: "Through mid-May",
    location: "La Jolla — Children's Pool",
    image: sealImg,
    sources: sealSet,
    imageAlt:
      "Harbor seals and pups resting on a sandy San Diego cove with rocky outcrops and gentle surf",
    imageWidth: 1920,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/harbor-seal-pupping" },
    pageHref: "/happening/harbor-seal-pupping",
  },
  {
    id: "late-may-grunion",
    startDate: "05-16",
    endDate: "05-31",
    priority: 2,
    badge: "Tonight",
    title: "Grunion Season Brings Moonlit Beach Nights",
    body:
      "After select new and full moons, silvery grunion ride the highest waves onto sandy beaches to spawn. Watch quietly, use red or dim light, and check current rules before any take.",
    date: "Select nights after new & full moons",
    location: "Mission Beach to Silver Strand",
    image: grunionImg,
    sources: grunionSet,
    imageAlt:
      "Silvery grunion fish spawning on a moonlit San Diego sandy beach at night with the moon overhead",
    imageWidth: 1920,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/late-may-grunion" },
    pageHref: "/happening/late-may-grunion",
  },
  {
    id: "june-beach-season",
    startDate: "06-01",
    endDate: "06-15",
    badge: "Summer Start",
    title: "Beach Season Starts With Long Evenings on the Sand",
    body:
      "June brings longer daylight, volleyball at Mission and Pacific Beach, and mellow evening walks once the marine layer burns off. Pack layers for cool sunsets near the water.",
    date: "Early June",
    location: "Mission Beach & Pacific Beach",
    image: laJollaCoveImg,
    imageAlt: "Clear water and rocky shoreline at La Jolla Cove on a bright coastal day",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/june-beach-season" },
    pageHref: "/happening/june-beach-season",
  },
  {
    id: "june-paddling",
    startDate: "06-16",
    endDate: "06-30",
    badge: "On the Water",
    title: "Kayaks and Paddleboards Take Over the Bays",
    body:
      "Protected water in Mission Bay, La Jolla Shores, and Coronado makes late June a dependable window for beginner paddles, sea cave tours, and relaxed sunset sessions.",
    date: "Late June",
    location: "La Jolla Shores, Mission Bay & Coronado",
    image: homepageKayakHeroImg,
    imageAlt:
      "View from inside a La Jolla sea cave looking out at a flotilla of red tandem kayaks on calm turquoise water",
    imageWidth: 1600,
    imageHeight: 1056,
    cta: { label: "Read the full guide", to: "/happening/kayaks-and-paddleboards" },
    pageHref: "/happening/kayaks-and-paddleboards",
  },
  {
    id: "july-4th-fireworks",
    startDate: "06-28",
    endDate: "07-06",
    priority: 3,
    badge: "This Week",
    title: "July 4th on the Coast — Big Bay Boom, Piers & Bonfires",
    body:
      "Four synchronized fireworks barges light up San Diego Bay at 9 PM, with pier shows at Ocean Beach, Oceanside, and Imperial Beach. Local's guide to viewing spots, parking, transit, safety rules, and the smartest exit routes.",
    date: "July 4 — 9:00 PM show",
    location: "San Diego Bay, Coronado, OB & Oceanside",
    image: july4thHeroImg,
    imageAlt:
      "Big Bay Boom fireworks bursting over the San Diego skyline and harbor at night",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/july-4th-fireworks" },
    pageHref: "/happening/july-4th-fireworks",
  },
  {
    id: "bioluminescent-waves-watch",
    startDate: "07-07",
    endDate: "07-10",
    priority: 4,
    badge: "This Week",
    title: "Glowing Waves Return — Bioluminescent Red Tide Lights Up the Coast",
    body:
      "A dense Lingulodinium polyedra bloom is turning San Diego's shorebreak electric blue after dark. Dark moon, glassy nights, and small shorebreak all line up this week — here's where, when, and how to see it (and photograph it) safely.",
    date: "July 7 – 10 nights, ~9:15pm onward",
    location: "Torrey Pines, La Jolla, Sunset Cliffs & Coronado",
    image: bioluminescentHeroImg,
    imageAlt:
      "Electric-blue bioluminescent waves breaking on a dark San Diego beach at night",
    imageWidth: 1600,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/bioluminescent-waves-watch" },
    pageHref: "/happening/bioluminescent-waves-watch",
  },
  {
    id: "july-snorkeling",
    startDate: "07-11",
    endDate: "07-31",
    badge: "Warm Water",
    title: "La Jolla Snorkeling Season Hits Its Stride",
    body:
      "Clearer summer water brings leopard sharks, garibaldi, rays, and bright kelp-edge views near La Jolla Shores and the Cove. Go early before afternoon wind and crowds arrive.",
    date: "Mid to late July",
    location: "La Jolla Shores & La Jolla Cove",
    image: snorkelingImg,
    imageAlt: "Snorkeler exploring clear blue water near the La Jolla coast",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/july-snorkeling" },
    pageHref: "/happening/july-snorkeling",
  },
  {
    id: "august-warm-water",
    startDate: "08-01",
    endDate: "08-15",
    badge: "Peak Summer",
    title: "Warm-Water Beach Days Stretch Into August",
    body:
      "Ocean temperatures are usually near their seasonal highs, making morning swims, sunset paddles, and relaxed beach days especially inviting from Coronado to La Jolla.",
    date: "Early August",
    location: "Coronado to La Jolla",
    image: paddleboardImg,
    imageAlt: "Paddleboarder on calm coastal water during a warm San Diego summer day",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/august-warm-water" },
    pageHref: "/happening/august-warm-water",
  },
  {
    id: "august-summer-surf",
    startDate: "08-16",
    endDate: "09-20",
    priority: 2,
    badge: "Surf Watch",
    title: "Late-Summer Surf Watch — South Swell & the Year's Warmest Water",
    body:
      "The last south swells of the season are still arriving, the water is at its annual peak of 68–72°F, and mornings are glassy until the wind fills in. Here's where to paddle out, how to read the forecast, and the beginner-friendly picks right now.",
    date: "Mid-August through late September",
    location: "Black's, Windansea, Sunset Cliffs & Tourmaline",
    image: lateSummerSurfImg,
    imageAlt:
      "Glassy late-summer wave peeling along the San Diego coast at sunrise with surfers in the lineup",
    imageWidth: 1600,
    imageHeight: 912,
    cta: { label: "Read the full guide", to: "/happening/august-summer-surf" },
    pageHref: "/happening/august-summer-surf",
  },

  {
    id: "september-local-summer",
    startDate: "09-01",
    endDate: "09-20",
    badge: "Local Summer",
    title: "September Brings San Diego's Quieter Beach Season",
    body:
      "Crowds thin after Labor Day while warm water and clear afternoons often remain. It is a favorite window for unhurried beach days, patio dinners, and golden-hour walks.",
    date: "Early to mid-September",
    location: "Del Mar, La Jolla & Coronado",
    image: coronadoImg,
    imageAlt: "Wide Coronado beach shoreline on a clear late-summer day",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/september-local-summer" },
    pageHref: "/happening/september-local-summer",
  },
  {
    id: "september-harbor-evenings",
    startDate: "09-21",
    endDate: "09-30",
    badge: "Golden Hour",
    title: "Harbor Evenings Feel Best After the Summer Rush",
    body:
      "Waterfront paths, ferry rides, and bayfront dining feel easier once peak-season crowds fade. Aim for sunset around the Embarcadero, Coronado Ferry Landing, or Shelter Island.",
    date: "Late September",
    location: "San Diego Bay & Coronado",
    image: paddleboardImg,
    imageAlt: "Calm San Diego bay water with a paddleboard at golden hour",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/september-harbor-evenings" },
    pageHref: "/happening/september-harbor-evenings",
  },
  {
    id: "october-fall-sunsets",
    startDate: "10-01",
    endDate: "10-15",
    badge: "Fall Light",
    title: "October Sunsets Bring Clear Coastal Views",
    body:
      "Fall often delivers crisp horizons and calmer evenings along Sunset Cliffs, Windansea, and Torrey Pines. Bring a light layer and arrive before parking fills near sunset.",
    date: "Early October",
    location: "Sunset Cliffs, Windansea & Torrey Pines",
    image: sunsetCliffsImg,
    imageAlt: "Sunset Cliffs coastline glowing at sunset with waves below",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/october-fall-sunsets" },
    pageHref: "/happening/october-fall-sunsets",
  },
  {
    id: "october-tidepool-return",
    startDate: "10-16",
    endDate: "10-31",
    badge: "Low Tide",
    title: "Tide Pool Season Starts Coming Back Into View",
    body:
      "As fall tides improve, rocky shelves around Cabrillo, La Jolla, and Sunset Cliffs become more accessible again. Time visits carefully and step only on bare rock.",
    date: "Late October",
    location: "Cabrillo, La Jolla & Sunset Cliffs",
    image: tidesImg,
    sources: tidesSet,
    imageAlt:
      "Tide pools at Sunset Cliffs San Diego at low tide during golden hour, with a starfish and exposed rocky reef",
    imageWidth: 1920,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/october-tidepool-return" },
    pageHref: "/happening/october-tidepool-return",
  },
  {
    id: "november-winter-swell",
    startDate: "11-01",
    endDate: "11-15",
    badge: "Surf Season",
    title: "Winter Swell Season Starts to Wake Up",
    body:
      "Northwest swells become more common in November, sharpening conditions at exposed breaks. Watch forecasts closely and give experienced surfers room when size builds.",
    date: "Early November",
    location: "Blacks, Ocean Beach & North County",
    image: surfingImg,
    imageAlt: "Surfer on a powerful winter swell along the San Diego coastline",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/november-winter-swell" },
    pageHref: "/happening/november-winter-swell",
  },
  {
    id: "november-coastal-hikes",
    startDate: "11-16",
    endDate: "11-30",
    badge: "Cool Weather",
    title: "Cooler Days Make Coastal Hikes Easy Again",
    body:
      "Late November is a comfortable time for Torrey Pines, Bayside Trail, Sunset Cliffs, and lagoon paths, especially when inland heat fades and ocean air stays mild.",
    date: "Late November",
    location: "Torrey Pines, Point Loma & coastal lagoons",
    image: torreyPinesImg,
    imageAlt: "Torrey Pines trail with coastal cliffs and Pacific views on a cool clear day",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/november-coastal-hikes" },
    pageHref: "/happening/november-coastal-hikes",
  },
  {
    id: "december-holiday-coast",
    startDate: "12-01",
    endDate: "12-14",
    badge: "Holiday Coast",
    title: "December Coast Walks Pair With Holiday Lights",
    body:
      "Shorter days make sunset plans simple: walk the waterfront, catch golden light near Coronado or La Jolla, then head inland or bayside for holiday lights and dinner.",
    date: "Early December",
    location: "Coronado, La Jolla & San Diego Bay",
    image: coronadoImg,
    imageAlt: "Coronado coastline in soft winter light with wide beach and ocean views",
    ...standardImageSize,
    cta: { label: "Read the full guide", to: "/happening/december-holiday-coast" },
    pageHref: "/happening/december-holiday-coast",
  },
  {
    id: "december-king-tides",
    startDate: "12-15",
    endDate: "12-31",
    priority: 2,
    badge: "This Week",
    title: "King Tides Return to the San Diego Coast",
    body:
      "Late-December king tides bring dramatic high-water views and very low tide-pool windows. Check the tide chart before heading to low-lying paths, beaches, or rocky shelves.",
    date: "Late December",
    location: "Sunset Cliffs, Cabrillo & La Jolla",
    image: tidesImg,
    sources: tidesSet,
    imageAlt:
      "Tide pools at Sunset Cliffs San Diego at low tide during golden hour, with a starfish and exposed rocky reef",
    imageWidth: 1920,
    imageHeight: 1088,
    cta: { label: "Read the full guide", to: "/happening/december-king-tides" },
    pageHref: "/happening/december-king-tides",
  },
];

const toMonthDay = (date: string) => date.slice(-5);

/** Returns true if `now` falls within the recurring [startDate, endDate] month/day window. */
const isActive = (story: HappeningStory, now: Date): boolean => {
  const today = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const start = toMonthDay(story.startDate);
  const end = toMonthDay(story.endDate);

  if (start <= end) return today >= start && today <= end;
  return today >= start || today <= end;
};

/** Pick the active story for `now`, breaking ties by highest priority then queue order. */
export const getCurrentStory = (now: Date = new Date()): HappeningStory => {
  const active = stories.filter((s) => isActive(s, now));
  if (active.length === 0) return stories[0];
  return active.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0];
};
